import { Dispatch, GetState } from "@reduxjs/toolkit";
import hvpAuthenticatedApi from "../../api/hvpAuthenticatedApi";
import { ApiResponse } from "../../shared/types";
import { LoginResponse } from "../../shared/types/authTypes";
import {
  loginFailure,
  loginSuccess,
  requestLogin,
  setLoading,
  logout,
} from "../slice/authSlice";
import hvpPublicApi from "../../api/hvpPublicApi";
import { shouldTryDevAutoLogin } from "./dev-auto-login";
import {
  fireSwalSuccess,
  getCollaboratorById,
  handleError,
} from "../../helpers";
const authRoute = "/auth";
export const startCheckingNew = () => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const token = localStorage.getItem("token");

      // Local development only — see dev-auto-login.ts
      if (shouldTryDevAutoLogin(import.meta.env, token)) {
        if (await devAutoLogin(dispatch)) return;
      }

      if (!token) {
        return dispatch(setLoading(false));
      }
      const { data } = await hvpAuthenticatedApi.post<
        ApiResponse<LoginResponse>
      >(`${authRoute}/collaborator/refresh`);

      if (data.ok) {
        localStorage.setItem("token", data.data.token);
        localStorage.setItem(
          "token-init-date",
          new Date().getTime().toString()
        );
      }
      dispatch(loginSuccess(data.data.user));
    } catch (error: any) {
      // A stale token in dev (expired, or from another database): try the dev sign-in before failing
      if (shouldTryDevAutoLogin(import.meta.env, null) && (await devAutoLogin(dispatch).catch(() => false))) return;
      dispatch(loginFailure(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  };
};

/** Local development only: sign in through the backend dev route. True when it worked */
const devAutoLogin = async (dispatch: Dispatch): Promise<boolean> => {
  const { data } = await hvpPublicApi.post<ApiResponse<LoginResponse>>(`${authRoute}/collaborator/dev-login`);
  if (!data.ok) return false;
  localStorage.setItem("token", data.data.token);
  localStorage.setItem("token-init-date", new Date().getTime().toString());
  dispatch(loginSuccess(data.data.user));
  return true;
};

export const login = (email: string, password: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const { data } = await hvpPublicApi.post<ApiResponse<LoginResponse>>(
        `${authRoute}/collaborator/login`,
        { email, password }
      );
      if (data.ok) {
        localStorage.setItem("token", data.data.token);
        localStorage.setItem(
          "token-init-date",
          new Date().getTime().toString()
        );
      }
      const { uid, col_code, role, image_url } = data.data.user;
      dispatch(
        loginSuccess({
          uid,
          col_code: col_code,
          role,
          image_url,
        })
      );
    } catch (error: any) {
      dispatch(loginFailure(error.message));
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const startLoggingOut = () => {
  return async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const { data } = await hvpAuthenticatedApi.post<
        ApiResponse<LoginResponse>
      >(`${authRoute}/collaborator/logout`);
      if (data.ok) {
        localStorage.removeItem("token");
        localStorage.removeItem("token-init-date");
        dispatch(logout());
      }
    } catch (error: any) {
      dispatch(loginFailure(error.message));
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const collaboratorPasswordChange = (
  password: string,
  newPassword: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));
      const { data } = await hvpAuthenticatedApi.patch<
        ApiResponse<LoginResponse>
      >(`${authRoute}/collaborator/change-password`, { password, newPassword });
      if (data.ok) {
        await fireSwalSuccess("Password changed successfully");
      }
      return true;
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const changeUserCredentialsByEmailAndPassword = (
  email: string,
  password: string
) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));

      // 1. Authenticate the NEW user (public endpoint, no token needed).
      const { data } = await hvpPublicApi.post<ApiResponse<LoginResponse>>(
        `${authRoute}/collaborator/login`,
        { email, password }
      );
      if (!data.ok) {
        throw new Error("No se pudo iniciar sesión con el nuevo usuario");
      }

      // 2. Best-effort: log out the PREVIOUS user for the audit trail. This
      //    runs BEFORE we overwrite the token, so the interceptor still sends
      //    the old user's token. It MUST NOT block the switch — on a shared
      //    terminal the previous token is often expired and would 401, which
      //    used to abort the whole switch and leave the app on the old user.
      try {
        await hvpAuthenticatedApi.post<ApiResponse<LoginResponse>>(
          `${authRoute}/collaborator/logout`
        );
      } catch {
        // Logging out the old session is not critical to switching users.
      }

      // 3. Commit the switch: replace the token and the active user.
      localStorage.setItem("token", data.data.token);
      localStorage.setItem("token-init-date", new Date().getTime().toString());
      const { uid, col_code, role, image_url } = data.data.user;
      dispatch(
        loginSuccess({
          uid,
          col_code: col_code,
          role,
          image_url,
        })
      );
      await fireSwalSuccess("Usuario cambiado correctamente");
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
};

export const changeUserCredentialsById = (collaboratorId: string) => {
  return async (dispatch: Dispatch) => {
    try {
      dispatch(setLoading(true));

      // 1. Open a session as the target. Needs the current admin session (or one an admin opened
      //    by switching); the new session carries the target's real role.
      const { data } = await hvpAuthenticatedApi.post<ApiResponse<LoginResponse>>(
        `${authRoute}/collaborator/simplified-login`,
        { collaboratorId }
      );
      if (!data.ok) {
        throw new Error("No se pudo cambiar de usuario");
      }

      // 2. Best-effort logout of the previous user (audit trail only, must not
      //    block the switch). Runs before the token is replaced.
      try {
        await hvpAuthenticatedApi.post<ApiResponse<LoginResponse>>(
          `${authRoute}/collaborator/logout`
        );
      } catch {
        // Logging out the old session is not critical to switching users.
      }

      // 3. Commit the switch.
      localStorage.setItem("token", data.data.token);
      localStorage.setItem("token-init-date", new Date().getTime().toString());
      const { uid, col_code, role, image_url, impersonatorUid } = data.data.user;
      dispatch(
        loginSuccess({
          uid,
          col_code: col_code,
          role,
          image_url,
          impersonatorUid,
        })
      );
      await fireSwalSuccess("Usuario cambiado correctamente");
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(setLoading(false));
    }
  };
};
